import {DateTime} from 'luxon';
import React, {FC, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useBuildCancelToken} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {tryFindConferenceJoinUrl} from '../../../../helpers/models/conferenceHelpers';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {fetchConferenceAsync} from '../../../../services/calendar/conference';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {DropdownItemIcon} from '../../../common/dropdowns/dropdownItemIcon';
import {IntegrationIcon, IntegrationIconsEnum} from '../../../common/integrationIcon';
import {CommentComposerIcon} from './commentComposerIcon';

/*
 * Props.
 */

interface CommentComposerAddContentProps {
  conversationId: number | undefined;
  isDisabled?: boolean;
  onFileUpload: HandlerOf<File>;
  onFileDropdownOpen: HandlerOf<boolean | undefined>;
  zoomIntegration: IntegrationModel | undefined;
  onInsertCommentComposerText: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledLoaderWrapperDiv = styled.div`
  height: 18px;
  width: 18px;
  position: relative;

  /* Compensate for the fact that our loader is slightly larger than 20x20.*/
  div {
    position: absolute;
    left: -1px;
    top: -1px;
  }
`;

const StyledIconDiv = styled.div`
  grid-area: left-icon;
  align-self: center;
  margin: -3px 8px -3px -3px;
  padding: 2px;
  border-radius: 12px;
`;

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

/*
 * Component.
 */

export const CommentComposerAddContent: FC<CommentComposerAddContentProps> = (props) => {
  const {onFileUpload, onFileDropdownOpen, onInsertCommentComposerText} = props;
  const {conversationId, isDisabled, zoomIntegration} = props;

  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {buildCancelToken} = useBuildCancelToken();
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADED);

  const {copy} = useClipboard();

  /*
   * Event handlers.
   */

  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      // For each file in the input, add it.
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        fileList.forEach((f) => onFileUpload(f));
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileUpload, setInputKey],
  );

  const onAddFileClick = useCallback(() => {
    onFileDropdownOpen(undefined);

    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_ADD_FILES_ICON_CLICK, {
      conversation_id: conversationId,
    });
  }, [conversationId, onFileDropdownOpen]);

  const onAddZoomClick = async () => {
    if (!zoomIntegration) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_ADD_ZOOM);

    setStatus(AsyncStatusesEnum.LOADING);

    const getJoinUrl = async () => {
      const conference = await fetchConferenceAsync(
        zoomIntegration,
        '',
        DateTime.local(),
        DateTime.local().plus({minutes: 60}),
        buildCancelToken(),
      );

      if (conference) {
        const joinUrl = tryFindConferenceJoinUrl(conference);

        if (joinUrl) {
          onInsertCommentComposerText(`Join the Zoom meeting: ${joinUrl}`);
        }

        setStatus(AsyncStatusesEnum.LOADED);

        if (joinUrl) {
          return joinUrl;
        }

        throw new ErrorWithData('Could not find conference join url', {conference});
      }

      setStatus(AsyncStatusesEnum.FAILED);

      throw new Error('Could not fetch conference');
    };

    copy(getJoinUrl());
  };

  const onPopoverToggle: HandlerOf<boolean> = (isOpen) => {
    if (!isOpen) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_PLUS_ICON_CLICK, {
      conversation_id: conversationId,
    });
  };

  /*
   * Render.
   */

  const renderButton: RendererOf<boolean> = (isOpen) => (
    <CommentComposerIcon iconName="plusCircleSmall" isDisabled={isDisabled} hasHover={isOpen} />
  );

  const renderDropdown: RendererOf<PopoverTransferProps> = (popoverProps) => (
    <Dropdown {...popoverProps} placement="top-end">
      <DropdownList {...popoverProps}>
        <DropdownItem onSelect={onAddFileClick}>
          <DropdownItemIcon label={null} iconName="attachmentSmall" />
          <DropdownItemContent>
            <Trans id="comment-composer-add-files">Add files</Trans>
          </DropdownItemContent>
        </DropdownItem>
        <DropdownItem onSelect={onAddZoomClick}>
          <StyledIconDiv>
            <IntegrationIcon integrationIcon={IntegrationIconsEnum.ZOOM} />
          </StyledIconDiv>
          <DropdownItemContent>
            <Trans id="comment-composer-add-zoom-meeting">Add a Zoom meeting</Trans>
          </DropdownItemContent>
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );

  const renderButtonContent = () => {
    // If we don't have a Zoom integration enabled, just show the upload attachments icon.
    if (!zoomIntegration) {
      return (
        <ChromelessButton shouldPreventMouseDown isDisabled={isDisabled} onClick={onAddFileClick}>
          <CommentComposerIcon
            iconName="attachmentSmall"
            isDisabled={isDisabled}
            renderTooltip={() => !isDisabled && <Trans id="composer-comment-attachments">Add files</Trans>}
          />
        </ChromelessButton>
      );
    }

    // If we're creating a Zoom meeting, show a loader.
    if (status === AsyncStatusesEnum.LOADING) {
      return (
        <StyledLoaderWrapperDiv>
          <Loader size={VisualSizesEnum.SMALL} />
        </StyledLoaderWrapperDiv>
      );
    }

    // Otherwise show a plus icon that allows the user to add files or zoom.
    return (
      <PopoverCoordinator
        shouldPreventMouseDown
        isDisabled={isDisabled}
        renderButton={renderButton}
        renderModal={renderDropdown}
        onPopoverToggle={onPopoverToggle}
      />
    );
  };

  return (
    <>
      <StyledFileLabel>
        <input ref={fileInputRef} key={inputKey} type="file" onChange={onFileInputChange} multiple />
      </StyledFileLabel>
      {renderButtonContent()}
    </>
  );
};
